import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

const CompaniesContext = createContext({});
const SELECTION_STORAGE_KEY = "@HubEverlog:companySelection";
const ALL_COMPANIES_OPTION_VALUE = null;
const ALL_COMPANIES_OPTION_LABEL = "Todos os embarcadores";

// PO1-2286: a transportadora também escolhe "Todos os embarcadores" (o
// backend usa a lista de vínculos ativos quando não vem embarcador).
export const canUseAllCompaniesOption = (user, empresas = []) => {
  return empresas.length > 0;
};

// PO1-2286: para o setor transportadora, a transportadora do topo é fixa (a
// do login, expandida para a raiz no servidor) e não tem seletor.
export const isTransportadoraFixa = (user) => Boolean(user && user.setor === "transportadora");

export const buildTransportadoraFixa = (user) => {
  if (!isTransportadoraFixa(user) || !user.transportadoraId) return null;
  return {
    value: user.transportadoraId,
    label: user.transportadoraNome || `Transportadora ${user.transportadoraId}`,
    fixa: true,
  };
};

const buildSelectionState = ({ empresa, transportadora }) => {
  const empresaId = getOptionValue(empresa);
  const transportadoraId = getOptionValue(transportadora);
  const isAllEmpresas = Boolean(empresa) && empresaId === ALL_COMPANIES_OPTION_VALUE;
  const isAllTransportadoras = transportadoraId === null || transportadoraId === undefined;
  const scalarParams = {};
  const arrayParams = {};

  if (empresaId !== null && empresaId !== undefined) {
    scalarParams.embarcadorId = empresaId;
    arrayParams.embarcadores = [empresaId];
  }

  if (transportadoraId !== null && transportadoraId !== undefined) {
    scalarParams.transportadoraId = transportadoraId;
    arrayParams.transportadoras = [transportadoraId];
  }

  return {
    empresaId,
    transportadoraId,
    isAllEmpresas,
    isAllTransportadoras,
    hasEmpresaSelection: empresaId !== null && empresaId !== undefined,
    hasTransportadoraSelection:
      transportadoraId !== null && transportadoraId !== undefined,
    scalarParams,
    arrayParams,
  };
};

const getOptionValue = (option) => {
  if (!option) {
    return null;
  }

  if (Object.prototype.hasOwnProperty.call(option, "value")) {
    return option.value;
  }

  return option.id ?? option.Id ?? option.label ?? null;
};

const findOptionByValue = (options = [], value) => {
  if (value === undefined) {
    return null;
  }

  return (
    options.find(
      (option) => String(getOptionValue(option)) === String(value)
    ) || null
  );
};

const readStoredSelection = () => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const rawValue = window.localStorage.getItem(SELECTION_STORAGE_KEY);

    if (!rawValue) {
      return {};
    }

    const parsed = JSON.parse(rawValue);
    if (parsed?.empresaValue === ALL_COMPANIES_OPTION_LABEL) {
      parsed.empresaValue = ALL_COMPANIES_OPTION_VALUE;
    }

    return parsed;
  } catch (error) {
    return {};
  }
};

const writeStoredSelection = (empresaValue, transportadoraValue) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      SELECTION_STORAGE_KEY,
      JSON.stringify({
        empresaValue: empresaValue ?? null,
        transportadoraValue: transportadoraValue ?? null,
      })
    );
  } catch (error) {
    // Ignore storage failures and keep the selection only in memory.
  }
};

const buildTransportadoraOptions = (empresas = []) => {
  const seen = new Set();

  return empresas.reduce((acc, empresa) => {
    (empresa?.transportadoras || []).forEach((transportadora) => {
      const transportadoraValue = getOptionValue(transportadora);
      if (transportadoraValue === null || transportadoraValue === undefined) {
        return;
      }

      const key = String(transportadoraValue);
      if (seen.has(key)) {
        return;
      }

      seen.add(key);
      acc.push(transportadora);
    });

    return acc;
  }, []);
};

export const buildEmpresaOptions = (empresas = [], user) => {
  if (!canUseAllCompaniesOption(user, empresas)) {
    return empresas;
  }

  return [
    {
      label: ALL_COMPANIES_OPTION_LABEL,
      value: ALL_COMPANIES_OPTION_VALUE,
      transportadoras: buildTransportadoraOptions(empresas),
      isAllOption: true,
    },
    ...empresas,
  ];
};

export const CompaniesProvider = ({ children }) => {
  const [hasHydratedSelection, setHasHydratedSelection] = useState(false);
  const [empresa, setEmpresa] = useState(null);
  const [optionsEmpresa, setOptionsEmpresa] = useState(null);
  const [transportadora, setTransportadora] = useState(null);
  const [optionsTransportadora, setOptionsTransportadora] = useState(null);

  useEffect(() => {
    if (!hasHydratedSelection) {
      return;
    }

    writeStoredSelection(getOptionValue(empresa), getOptionValue(transportadora));
  }, [empresa, hasHydratedSelection, transportadora]);

  const loadCompanies = useCallback((user) => {
    const empresas = user?.empresas || [];
    const empresaOptions = buildEmpresaOptions(empresas, user);

    if (!empresas.length) {
      setHasHydratedSelection(false);
      setEmpresa(null);
      setOptionsEmpresa(null);
      setTransportadora(null);
      setOptionsTransportadora(null);
      return;
    }

    const storedSelection = readStoredSelection();
    // Seleção guardada fora de `user.empresas` é descartada (PO1-2286): cai na
    // primeira empresa, nunca num embarcador que o login não enxerga.
    const selectedEmpresa =
      findOptionByValue(empresaOptions, storedSelection.empresaValue) ||
      empresas[0];
    const fixa = buildTransportadoraFixa(user);
    const transportadoras = fixa ? [fixa] : selectedEmpresa?.transportadoras || [];
    const selectedTransportadora = fixa
      ? fixa
      : findOptionByValue(transportadoras, storedSelection.transportadoraValue);

    setEmpresa(selectedEmpresa);
    setOptionsEmpresa(empresaOptions);
    setTransportadora(selectedTransportadora);
    setOptionsTransportadora(transportadoras);
    setHasHydratedSelection(true);
  }, []);

  const selectEmpresa = useCallback((selectedEmpresa) => {
    const nextEmpresa =
      selectedEmpresa ||
      findOptionByValue(optionsEmpresa || [], ALL_COMPANIES_OPTION_VALUE);

    setEmpresa(nextEmpresa || null);
    setTransportadora((currentTransportadora) => {
      // Transportadora fixa (setor transportadora) não muda com o embarcador.
      if (currentTransportadora && currentTransportadora.fixa) return currentTransportadora;
      const transportadoras = nextEmpresa?.transportadoras || [];
      setOptionsTransportadora(transportadoras);
      return findOptionByValue(
        transportadoras,
        getOptionValue(currentTransportadora)
      );
    });
  }, [optionsEmpresa]);

  const selectTransportadora = useCallback((selectedTransportadora) => {
    setTransportadora(selectedTransportadora || null);
  }, []);

  const selectionState = buildSelectionState({ empresa, transportadora });

  return (
    <CompaniesContext.Provider
      value={{
        empresa,
        optionsEmpresa,
        transportadora,
        optionsTransportadora,
        loadCompanies,
        selectEmpresa,
        selectTransportadora,
        ...selectionState,
      }}
    >
      {children}
    </CompaniesContext.Provider>
  );
};

export const useCompanies = () => {
  const context = useContext(CompaniesContext);

  if (!context) {
    throw new Error("useCompanies must be used within an AuthProvider");
  }
  return context;
};
